import React, { Fragment } from 'react';

import type { ExtensionParams } from '@atlaskit/editor-common/extensions/extension-handler';
import type { EditorContentMode } from '@atlaskit/editor-common/types/editor-appearance';
import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';

import { EXTENSION_KEY } from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { createMacroExtensionHandler } from '@confluence/fabric-extension-lib/entry-points/createMacroExtensionHandler.ts';
import { expVal } from '@confluence/feature-experiments';
import {
	RENDERER,
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceStart } from '@confluence/macro-tracker/entry-points/MacroExperienceStart.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { InlineExternalImage } from '@confluence/inline-external-image/entry-points/InlineExternalImage.ts';
import type { InlineExternalImageParams } from '@confluence/inline-external-image/entry-points/InlineExternalImage.ts';
import { LoadablePlaceholderServer } from '@confluence/loadable/entry-points/LoadablePlaceholder.ts';

import { LegacyContentExtensionHandlerPaint } from '../LegacyContentExtension/index.ts';

import { isInitialLcmSsrEligible } from './lcmSsrEligibility.ts';
import { PlaceholderAwareInlineNodeRendererWrapper } from './PlaceholderAwareInlineNodeRendererWrapper.tsx';

export const getSsrMigrationExtHandlerForRenderer = ({
	contentId,
	contentMode,
}: {
	contentId: any;
	contentMode?: EditorContentMode;
}): ((extension: ExtensionParams<any>, ...args: any[]) => React.ReactElement) =>
	createMacroExtensionHandler((extension) => {
		if (!extension) {
			return null;
		}
		const { type, extensionKey, parameters } = extension;

		const attributes = getMacroAttributesFromADFNode(extension);
		const macroId = attributes?.macroId;
		const defaultProps = {
			contentId,
			mode: RENDERER,
			extensionKey,
			attributes,
			node: extension,
		};

		let content: React.ReactNode | null = null;
		if (extensionKey === EXTENSION_KEY.INLINE_EXTERNAL_IMAGE) {
			const inlineExternalImageProps = parameters as InlineExternalImageParams;
			content = (
				<Fragment>
					<MacroExperienceStart
						name={getExperienceName(RENDERER, extension)}
						mode={RENDERER}
						extensionKey={extensionKey}
						attributes={attributes}
						timeout={ExperienceTimeout.MACRO_LOAD}
						contentId={contentId}
					/>
					<RendererActionsContext>
						<InlineExternalImage {...inlineExternalImageProps} {...defaultProps} />
					</RendererActionsContext>
				</Fragment>
			);
		}

		if (isInitialLcmSsrEligible(extension) && expVal('confluence_lcm_ssr', 'isEnabled', false)) {
			content = (
				<LegacyContentExtensionHandlerPaint
					contentId={contentId}
					contentMode={contentMode}
					node={extension}
				/>
			);
		}

		// This boundary is not directly used but it provides reference for fixing nested div under span.
		// See next/packages/ssr-scripts-utils/src/fixUnbalancedPlaceholderCommentMarkers.ts for details
		return (
			<LoadablePlaceholderServer placeholderId={`migration-ext-container-${macroId}`}>
				{type === 'inlineExtension' ? (
					// Mimic the logic in platform/packages/editor/renderer/src/ui/ExtensionRenderer.tsx
					// We don't update the platform code for 2 reasons:
					// 1. Backwards compatibility. I don't know if other product supplies this wrapper already (unlikely)
					// 2. More importantly this needs to be inside the migration-ext-container-${macroId} boundary.
					//    Inline macros has <span> nested in <div> problem. We are using the boundary as guide to fix them.
					//    See next/packages/ssr-scripts-utils/src/fixUnbalancedPlaceholderCommentMarkers.ts
					// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
					<PlaceholderAwareInlineNodeRendererWrapper macroId={macroId}>
						{content}
					</PlaceholderAwareInlineNodeRendererWrapper>
				) : (
					content
				)}
			</LoadablePlaceholderServer>
		);
	});
